/**
 * QL-8 — pure adjustment-scoring engine for config-driven risk-score
 * adjustment questions, shared by the API services and the web flow widget so
 * live previews and persisted results can never drift apart.
 *
 * Decimal semantics (QL-8): deltas may carry one decimal place. The deltas of
 * all answered, ENABLED questions are summed as decimals (no per-delta
 * truncation), the sum is rounded to one decimal place to absorb binary
 * floating-point noise (0.7 + 0.7 + 0.6 must be 2, not 1.999…), and the total
 * is truncated toward zero exactly once: ±0.5 ⇒ 0, ±1.5 ⇒ ±1, ±2.5 ⇒ ±2.
 *
 * These functions know nothing about the DB — callers pass the model's
 * questions + the client's answers.
 */

import type { RiskAdjustmentQuestion } from "./types/risk-models";

export type AdjustmentResult = {
  /** Raw decimal sum, rounded to 1 decimal — what gets persisted/audited. */
  sum: number;
  /** Truncation of `sum` toward zero — what scoring and displays apply. */
  applied: number;
};

/** Only the enabled questions participate in scoring/completeness. */
export function enabledQuestions(
  questions: RiskAdjustmentQuestion[] | null | undefined,
): RiskAdjustmentQuestion[] {
  return (questions ?? []).filter((q) => q.enabled);
}

/**
 * The signed decimal delta a single answered question contributes at
 * `initialScore`. Returns 0 when the question/answer/delta isn't found or the
 * delta isn't a finite number (a non-applicable combination contributes
 * nothing). Deltas are NOT truncated here — half-points must accumulate.
 */
export function answerDelta(
  question: RiskAdjustmentQuestion,
  answerId: string | undefined,
  initialScore: number,
): number {
  if (!answerId) return 0;
  const answer = question.answers.find((a) => a.id === answerId);
  if (!answer) return 0;
  const delta = answer.deltas?.[String(initialScore)];
  return typeof delta === "number" && Number.isFinite(delta) ? delta : 0;
}

/**
 * Collapse a decimal adjustment sum into the whole-number value that is
 * actually applied to the score: round to 1 decimal (floating-point guard),
 * then truncate toward zero. Readers of the persisted decimal sum use this
 * before display. The `+ 0` normalizes -0 (e.g. from a lone -0.5) to 0.
 */
export function truncateAdjustment(sum: number): number {
  return Math.trunc(Math.round(sum * 10) / 10) + 0;
}

/**
 * Sum the decimal deltas of every ENABLED question the client answered, at
 * the given initial score. Answers to disabled/unknown questions are ignored.
 * Returns both the raw decimal sum and its truncated applied value.
 */
export function computeAdjustment(
  questions: RiskAdjustmentQuestion[] | null | undefined,
  answers: Record<string, string> | null | undefined,
  initialScore: number,
): AdjustmentResult {
  const answered = answers ?? {};
  let sum = 0;
  for (const q of enabledQuestions(questions)) {
    sum += answerDelta(q, answered[q.id], initialScore);
  }
  // Round once at the end: 1-decimal deltas make the true sum 1-decimal, so
  // this only strips float noise, never real information. `+ 0` drops -0.
  const rounded = Math.round(sum * 10) / 10 + 0;
  return { sum: rounded, applied: truncateAdjustment(rounded) };
}

/**
 * The ENABLED questions that lack a valid answer in `answers` — missing
 * entirely, or referencing an answerId that isn't one of the question's
 * options. Callers that need to tell the user WHICH questions are outstanding
 * (e.g. the AI submit tool's rejection message) use this; boolean gates use
 * `allAdjustmentQuestionsAnswered`.
 */
export function unansweredAdjustmentQuestions(
  questions: RiskAdjustmentQuestion[] | null | undefined,
  answers: Record<string, string> | null | undefined,
): RiskAdjustmentQuestion[] {
  const answered = answers ?? {};
  return enabledQuestions(questions).filter((q) => {
    const answerId = answered[q.id];
    return !answerId || !q.answers.some((a) => a.id === answerId);
  });
}

/**
 * The keys of `answers` that are not the id of an ENABLED question — an unknown
 * id, or an answer to a disabled question. Scoring and completeness ignore such
 * answers, so a write carrying them is refused rather than stored: a caller's
 * typo would otherwise vanish and leave the real question unanswered. An answer
 * id that is not one of a known question's options is not stray; that is
 * `unansweredAdjustmentQuestions`' verdict.
 */
export function strayAdjustmentAnswerIds(
  questions: RiskAdjustmentQuestion[] | null | undefined,
  answers: Record<string, string> | null | undefined,
): string[] {
  const known = new Set(enabledQuestions(questions).map((q) => q.id));
  return Object.keys(answers ?? {}).filter((id) => !known.has(id));
}

/**
 * True iff every ENABLED question has an answer in `answers` that references
 * one of that question's answer options. Empty enabled set → trivially true.
 * This is the completeness gate for the risk_assessment component.
 */
export function allAdjustmentQuestionsAnswered(
  questions: RiskAdjustmentQuestion[] | null | undefined,
  answers: Record<string, string> | null | undefined,
): boolean {
  return unansweredAdjustmentQuestions(questions, answers).length === 0;
}
